/**
 * Money formatting for the whole app.
 *
 * One choke point, configured once per tenant. Everything here used to be a hardcoded `en-IN` /
 * `INR` formatter, which silently rendered a Dubai school's fees as rupees.
 */

type CurrencyDisplayConfig = {
  currencyCode: string;
  locale: string;
  /** ISO 3166-1 alpha-2, so phone validation can reach the tenant's country without a prop chain. */
  countryCode: string;
};

// Indian defaults, because that is where the platform started and because a tenant's real settings
// are not known until its details have loaded. Every value is overwritten by
// setCurrencyDisplayConfig on the first render after that.
const defaultConfig: CurrencyDisplayConfig = {
  currencyCode: "INR",
  locale: "en-IN",
  countryCode: "IN",
};

let config: CurrencyDisplayConfig = { ...defaultConfig };

// Intl.NumberFormat construction is not free and money is formatted in tight table loops, so the
// formatter is built once per configuration rather than per call.
let cachedFormatter: Intl.NumberFormat | null = null;
let cachedPreciseFormatter: Intl.NumberFormat | null = null;

function buildFormatters() {
  cachedFormatter = new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: config.currencyCode,
    maximumFractionDigits: 0,
  });

  cachedPreciseFormatter = new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: config.currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

buildFormatters();

/**
 * Points every money formatter at the tenant's currency and locale.
 *
 * Mirrors setDateDisplayConfig in lib/date.ts, and is called from the same place — the tenant
 * bootstrap — so dates and amounts can never disagree about which tenant is on screen.
 */
export function setCurrencyDisplayConfig(next: Partial<CurrencyDisplayConfig>): void {
  config = {
    currencyCode: next.currencyCode?.trim() || defaultConfig.currencyCode,
    locale: next.locale?.trim() || defaultConfig.locale,
    countryCode: next.countryCode?.trim() || defaultConfig.countryCode,
  };

  buildFormatters();
}

/** Resets to the platform default. Called on logout, when no tenant is in scope. */
export function resetCurrencyDisplayConfig(): void {
  config = { ...defaultConfig };
  buildFormatters();
}

/** The tenant's ISO 4217 currency code, for labels and exports. */
export function currentCurrencyCode(): string {
  return config.currencyCode;
}

/** The tenant's BCP-47 locale, for the few places that format something other than money. */
export function currentLocale(): string {
  return config.locale;
}

/**
 * The tenant's ISO 3166-1 alpha-2 country.
 *
 * Read by the forms that validate a phone number. Kept here rather than in a React context because
 * the validators are plain functions called from many places, and threading a prop through all of
 * them to answer "which country is this school in" would be a lot of noise for one value that never
 * varies within a session.
 */
export function currentCountryCode(): string {
  return config.countryCode;
}

/** Formats an amount in the tenant's currency, without decimals. */
export function formatMoney(value: number): string {
  return (cachedFormatter ?? new Intl.NumberFormat()).format(value);
}

/** Formats an amount in the tenant's currency, always with two decimals. */
export function formatMoneyPrecise(value: number): string {
  return (cachedPreciseFormatter ?? new Intl.NumberFormat()).format(value);
}

/**
 * @deprecated Use {@link formatMoney}. Kept so existing call sites keep working while they are
 * migrated; it is no longer rupee-specific despite the name, and formats in the tenant's currency.
 */
export function formatINR(value: number): string {
  return formatMoney(value);
}

/**
 * @deprecated Use {@link formatMoney}.
 */
export function formatCurrency(value: number): string {
  return formatMoney(value);
}
